import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { startLogin } from "@/lib/login";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User as UserIcon } from "lucide-react";

function errText(detail) {
  if (!detail) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return String(detail);
}

export default function Login() {
  const { user, loading, loginWithPassword, register } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!loading && user) navigate("/lobby", { replace: true }); }, [user, loading, navigate]);
  useEffect(() => { if (params.get("idle")) toast.info("Sua sessão foi encerrada por inatividade. Faça login novamente."); }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") await register(name, email, password);
      else await loginWithPassword(email, password);
      navigate("/lobby", { replace: true });
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="dr-grain min-h-screen bg-[#090B0E] flex items-center justify-center px-4">
      <div className="relative z-20 w-full max-w-md">
        <div className="text-center mb-6">
          {branding?.logo_url ? (
            <img src={branding.logo_url} alt={branding.brand_name} className="h-12 mx-auto mb-3 object-contain" />
          ) : null}
          <h1 className="font-head text-3xl font-black gold-text">{branding?.brand_name || "DOMINÓ ROYALLE"}</h1>
          <p className="text-[#9CA3AF] text-sm mt-1">Entre ou crie sua conta para jogar</p>
        </div>

        <div className="dr-card p-6" data-testid="login-card">
          <div className="flex gap-2 mb-5">
            <button onClick={() => setMode("login")} data-testid="tab-login"
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-colors ${mode === "login" ? "btn-gold" : "border border-white/10 text-[#9CA3AF]"}`}>Entrar</button>
            <button onClick={() => setMode("register")} data-testid="tab-register"
              className={`flex-1 py-2.5 rounded-full text-sm font-bold transition-colors ${mode === "register" ? "btn-gold" : "border border-white/10 text-[#9CA3AF]"}`}>Criar conta</button>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="flex items-center gap-2 bg-[#0F1319] border border-white/10 rounded-lg px-3 focus-within:border-[#D4AF37]">
                <UserIcon size={16} className="text-[#6b7280]" />
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" data-testid="reg-name"
                  className="flex-1 bg-transparent py-3 outline-none" />
              </div>
            )}
            <div className="flex items-center gap-2 bg-[#0F1319] border border-white/10 rounded-lg px-3 focus-within:border-[#D4AF37]">
              <Mail size={16} className="text-[#6b7280]" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="login-email"
                className="flex-1 bg-transparent py-3 outline-none" />
            </div>
            <div className="flex items-center gap-2 bg-[#0F1319] border border-white/10 rounded-lg px-3 focus-within:border-[#D4AF37]">
              <Lock size={16} className="text-[#6b7280]" />
              <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" placeholder="Senha" data-testid="login-password"
                className="flex-1 bg-transparent py-3 outline-none" />
            </div>
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full btn-gold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={16} className="animate-spin" /> : (mode === "register" ? "Criar conta" : "Entrar")}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-[#6b7280]">ou</span><div className="flex-1 h-px bg-white/10" />
          </div>

          <button onClick={startLogin} data-testid="google-login-btn"
            className="w-full py-3 rounded-full border border-white/15 font-semibold flex items-center justify-center gap-2 hover:bg-white/5 transition-colors">
            <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> Continuar com Google
          </button>

          <button onClick={() => navigate("/")} className="w-full text-center text-xs text-[#6b7280] mt-4 hover:text-[#9CA3AF]">Voltar ao início</button>
        </div>
      </div>
    </div>
  );
}
